import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const EMPTY = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm(EMPTY);
    setError("");
    setSubmitting(false);
  };

  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      close(false);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Failed to update password",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Change password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label
              htmlFor="cp-current"
              className="text-xs tracking-[0.15em] uppercase text-slate-600"
            >
              Current password
            </Label>
            <Input
              id="cp-current"
              type="password"
              required
              value={form.current}
              onChange={set("current")}
              autoComplete="current-password"
              className="mt-1.5"
              data-testid="cp-current-input"
            />
          </div>
          <div>
            <Label
              htmlFor="cp-next"
              className="text-xs tracking-[0.15em] uppercase text-slate-600"
            >
              New password
            </Label>
            <Input
              id="cp-next"
              type="password"
              required
              minLength={6}
              value={form.next}
              onChange={set("next")}
              autoComplete="new-password"
              className="mt-1.5"
              placeholder="At least 6 characters"
              data-testid="cp-next-input"
            />
          </div>
          <div>
            <Label
              htmlFor="cp-confirm"
              className="text-xs tracking-[0.15em] uppercase text-slate-600"
            >
              Confirm new password
            </Label>
            <Input
              id="cp-confirm"
              type="password"
              required
              minLength={6}
              value={form.confirm}
              onChange={set("confirm")}
              autoComplete="new-password"
              className="mt-1.5"
              data-testid="cp-confirm-input"
            />
          </div>
          {error && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="cp-error"
            >
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => close(false)}
              disabled={submitting}
              data-testid="cp-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="cp-submit-button"
            >
              {submitting ? "Saving..." : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
